// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import styled from 'styled-components';
import Button from '../Button';

export const Banner = styled.div`
  background:
    radial-gradient(1.5px 1.5px at 12% 22%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(1px 1px at 28% 64%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1.5px 1.5px at 41% 15%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 55% 48%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(2px 2px at 68% 24%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1px 1px at 76% 70%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1.5px 1.5px at 87% 38%, rgba(255, 255, 255, 0.75), transparent),
    radial-gradient(1px 1px at 94% 12%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1px 1px at 8% 82%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 4% 34%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(1.5px 1.5px at 17% 55%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 22% 8%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(2px 2px at 33% 42%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1px 1px at 36% 78%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1.5px 1.5px at 47% 62%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 51% 28%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1px 1px at 59% 9%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1.5px 1.5px at 63% 82%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1px 1px at 71% 50%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(1px 1px at 79% 16%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(2px 2px at 83% 58%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 91% 74%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1.5px 1.5px at 97% 44%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 3% 6%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 15% 92%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1px 1px at 44% 90%, rgba(255, 255, 255, 0.4), transparent),
    radial-gradient(1.5px 1.5px at 66% 38%, rgba(255, 255, 255, 0.35), transparent),
    radial-gradient(1px 1px at 88% 88%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(ellipse 90% 45% at 50% 108%, rgba(244, 104, 0, 0.28), transparent),
    linear-gradient(180deg, #0b0d12 0%, #12141c 100%);
`;

export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 56px 20px 64px;

  ${({ theme }) => theme.breakpoints.desktop} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 64px;
    padding: 96px 32px 104px;
  }
`;

export const TextContainer = styled.div`
  max-width: 560px;
`;

export const Eyebrow = styled.p`
  margin: 0 0 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grafanaOrangeLight};
`;

export const Title = styled.h1`
  margin: 0 0 20px;
  font-size: clamp(40px, 6vw, 68px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.colors.white};
`;

export const Subtitle = styled.p`
  margin: 0 0 32px;
  max-width: 460px;
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

export const GoShoppingButton = styled(Button)``;

export const GhostButton = styled(Button)`
  && {
    background: none;
    color: ${({ theme }) => theme.colors.white};
    border: 1px solid rgba(255, 255, 255, 0.4);
  }

  &&:hover {
    filter: none;
    box-shadow: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.7);
  }

  &&:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const Visual = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.desktop} {
    display: block;
    position: relative;
    flex-shrink: 0;
  }
`;

export const Porthole = styled.div<{ $src: string }>`
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.white} url(${({ $src }) => $src}) no-repeat center;
  background-size: 78%;
  border: 6px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.15),
    0 0 80px rgba(244, 104, 0, 0.25);
`;

export const FeaturedTag = styled.div`
  position: absolute;
  bottom: 26px;
  left: -18px;
  padding: 10px 18px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.ink};
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};

  span {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
    font-weight: 700;
  }
`;
